import type { Appearance } from "@townsy/contracts";

/**
 * Original Townsy art direction: bright daylight, coordinated warm pastels, cobalt + amber accents
 * (docs/01 section 2). Everything here is a plain hex string so the pure layout/test code can use
 * it without Babylon.
 */
export const TOKENS = {
  panel: "#FBF8F2",
  ink: "#1B1F2A",
  cobalt: "#2851D9",
  amber: "#D99A2B",
} as const;

export const SKY_COLOR = "#BDDFF8";
export const FOG_COLOR = "#D2E7F6";

export const GROUND = {
  grass: "#8DC46C",
  grassAlt: "#7EB65D",
  asphalt: "#5C6069",
  asphaltAlt: "#545860",
  paver: "#DCD6C9",
  paverLine: "#CBC4B5",
  plaza: "#E7D0AE",
  plazaAlt: "#DABF99",
  plazaLine: "#CBB089",
  curb: "#CFC8BA",
  paintWhite: "#F4F1E8",
  paintYellow: "#F1D27A",
  matCommercial: "#C8623A",
  matHome: "#D9B278",
  path: "#D7CDBB",
  woodFloor: "#C9A070",
  woodFloorAlt: "#B88E5F",
  tileFloor: "#E6E0D4",
  tileFloorAlt: "#D9D2C4",
  carpet: "#8C9BB5",
  carpetAlt: "#7F8EA8",
  stoneFloor: "#CFC8BB",
  stoneFloorAlt: "#BFB7A9",
  stoneMortar: "#9E968A",
} as const;

export interface BuildingColors {
  wall: string;
  wallAlt: string;
  pop: string;
  trim: string;
  roof: string;
  glass: string;
  door: string;
}

/** Ten coordinated facade palettes; buildings pick one by plot index. */
export const BUILDING_PALETTES: readonly BuildingColors[] = [
  { wall: "#F5E8D3", wallAlt: "#E9D8BA", pop: "#F2B8A0", trim: "#FFFFFF", roof: "#B85A42", glass: "#8EC6EC", door: "#4A5878" },
  { wall: "#DCE9F6", wallAlt: "#C6DAEE", pop: "#9CC5F0", trim: "#F9FBFF", roof: "#4C6E90", glass: "#A9D4F2", door: "#2E3D55" },
  { wall: "#DDE8CF", wallAlt: "#CBDBB8", pop: "#B9D7A0", trim: "#FBFCF7", roof: "#5E7A4A", glass: "#9FD0E8", door: "#3F4F3A" },
  { wall: "#F6DAD6", wallAlt: "#ECC4BE", pop: "#F4A9A0", trim: "#FFFFFF", roof: "#8A4C55", glass: "#9FCBEA", door: "#5A3A42" },
  { wall: "#F3E2B4", wallAlt: "#E8D193", pop: "#F0C45A", trim: "#FFFBEF", roof: "#8B6A2E", glass: "#9ECDEB", door: "#5A4A2A" },
  { wall: "#E6DEF2", wallAlt: "#D5C9EA", pop: "#C3ADE8", trim: "#FCFBFF", roof: "#5B4C80", glass: "#A6D0F0", door: "#3E3457" },
  { wall: "#D7EEE6", wallAlt: "#BEE1D5", pop: "#8FD5C0", trim: "#F8FDFB", roof: "#3F7A6A", glass: "#9CCDEA", door: "#2F4F46" },
  { wall: "#FAE1C9", wallAlt: "#F1CEAC", pop: "#F4B978", trim: "#FFFDF9", roof: "#A6603A", glass: "#9FCDEC", door: "#5C3E2A" },
  { wall: "#D9DEE6", wallAlt: "#C4CBD6", pop: "#A7B5CA", trim: "#F7F9FC", roof: "#3E4A5C", glass: "#A3CFEE", door: "#2C3442" },
  { wall: "#E7C8B6", wallAlt: "#D9B29C", pop: "#D98E6C", trim: "#FBF4EF", roof: "#6E3A2A", glass: "#9CCBEA", door: "#4B2E22" },
];

export interface HomeColors {
  wall: string;
  trim: string;
  roof: string;
  door: string;
  canvas: string;
}

export const HOME_PALETTES: readonly HomeColors[] = [
  { wall: "#F4E9D8", trim: "#FFFFFF", roof: "#8A4B3C", door: "#3F4F6A", canvas: "#E9A04E" },
  { wall: "#DCE7F2", trim: "#FFFFFF", roof: "#4D6A86", door: "#2F3D52", canvas: "#6FAF8A" },
  { wall: "#E9E2C8", trim: "#FFFDF6", roof: "#5F7A4C", door: "#4A3A2A", canvas: "#5FA4C8" },
  { wall: "#F3DCD4", trim: "#FFFFFF", roof: "#7E4A52", door: "#3E2F35", canvas: "#D9705C" },
  { wall: "#E3E6EC", trim: "#FFFFFF", roof: "#3C4858", door: "#2B3240", canvas: "#C9A35A" },
  { wall: "#E4EBD8", trim: "#FBFDF8", roof: "#6B5A3E", door: "#3C3A2E", canvas: "#8C7BC9" },
];

export const PROP_COLORS = {
  trunk: "#7B5232",
  trunkDark: "#6A4529",
  crownA: "#5EAE58",
  crownB: "#4E9D4A",
  crownC: "#79C063",
  crownD: "#6DB35A",
  lampPole: "#3E4450",
  lampHead: "#FFF2C2",
  benchWood: "#B9804F",
  benchLegs: "#3B3F47",
  planter: "#B9683F",
  bush: "#5FA653",
  flowerA: "#F06E8A",
  flowerB: "#F8D35A",
  flowerC: "#FFFFFF",
  stone: "#D8D1C4",
  stoneDark: "#B9B1A3",
  water: "#79C4E8",
  waterLight: "#B5E0F4",
  hedge: "#5C9D4E",
  fire: "#E8702A",
  log: "#6E4A2E",
  sign: "#FFFFFF",
  post: "#4B515C",
  metal: "#8E96A3",
} as const;

/** Shirt colours (Appearance.bodyColor). Mirrors the 2D builder swatches in ui/avatar/avatarModel.ts. */
export const BODY_COLOR_HEX: Record<Appearance["bodyColor"], string> = {
  cobalt: "#2851D9",
  coral: "#F0715B",
  moss: "#5F8F3E",
  amber: "#D99A2B",
  plum: "#7D44A6",
  teal: "#1E9C9C",
  slate: "#5C6C86",
  rose: "#D95A8F",
  white: "#F4F4F2",
  black: "#23252B",
};

/** Hair colours (Appearance.hairColor), same swatches as the 2D builder. */
export const HAIR_COLOR_HEX: Record<Appearance["hairColor"], string> = {
  black: "#1F1F24",
  brown: "#5A3820",
  auburn: "#8C3F1E",
  blonde: "#D9B36A",
  red: "#B8321F",
  grey: "#A8A49E",
  blue: "#2F6FD6",
  pink: "#E27AAE",
};

export const SKIN_TONE_HEX: Record<Appearance["skinTone"], string> = {
  s1: "#F7E1C9",
  s2: "#EAC39F",
  s3: "#D4A277",
  s4: "#AD7646",
  s5: "#7E5031",
  s6: "#52311E",
};

export const OUTFIT_COLORS = {
  denim: "#3F5B8C",
  dark: "#2B2F3C",
  shorts: "#F1F1F1",
  tights: "#5B4A62",
  white: "#F7F7F7",
  eye: "#1B1F2A",
  shoe: "#2A2A30",
  sneaker: "#ECECEC",
  lip: "#B5604F",
} as const;

/** Car paints (ambient traffic): coordinated with the town palette, no branding. */
export const CAR_PAINTS = ["#D9473B", "#2F6FD6", "#F2F0EA", "#E8B53A", "#3E8E5E", "#A9B0BA", "#2B2F3C", "#E9814B"] as const;

export const CAR_TRIM = {
  glass: "#3E5566",
  tyre: "#24262B",
  hub: "#B9BEC6",
  bumper: "#5A5F68",
  head: "#FFF4C8",
  tail: "#C8302B",
  interior: "#2F3237",
} as const;

/** Townsy Real Estate (civic office): warm brick and pale stone, deep green signage. */
export const CIVIC_COLORS = {
  brick: "#B5654A",
  brickDark: "#9C523B",
  stone: "#EDE4D3",
  stoneDark: "#D6CBB6",
  roof: "#8E8A84",
  glass: "#9FC9E6",
  door: "#2F4A3F",
  sign: "#1F5C4A",
  signAccent: "#F3E7C9",
} as const;

/** Empty lot markers and the For Sale sign. */
export const LOT_COLORS = {
  stake: "#F4EFE4",
  stakeTop: "#D9473B",
  hedge: "#5C9D4E",
  signPost: "#6E4A2E",
  signBack: "#F4EFE4",
} as const;

export const INTERIOR_COLORS = {
  wall: "#F4EEE3",
  wallAlt: "#E9E1D3",
  wainscot: "#C9B597",
  counter: "#A97C50",
  counterTop: "#E9DCC7",
  shelf: "#D9CDB9",
  shelfDark: "#B9A98F",
  register: "#3A4252",
  table: "#B98C5F",
  sofa: "#5C7FBF",
  sofaDark: "#4A68A0",
  rugAlt: "#F6F1E7",
  exit: "#2851D9",
  exitLit: "#F3F6FF",
  door: "#4A5878",
  product1: "#F06E8A",
  product2: "#F8D35A",
  product3: "#5FA4C8",
  product4: "#6FAF8A",
} as const;

/** Linear blend of two #RRGGBB colours (t = 0 -> a, 1 -> b). Invalid input returns `a`. */
export function mixHex(a: string, b: string, t: number): string {
  const pa = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(a);
  const pb = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(b);
  if (!pa || !pb) return a;
  const ch = (i: number) => {
    const v = Math.round(parseInt(pa[i]!, 16) * (1 - t) + parseInt(pb[i]!, 16) * t);
    return Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0");
  };
  return `#${ch(1)}${ch(2)}${ch(3)}`.toUpperCase();
}

/** Deterministic small hash for stable per-object variation (no Math.random in geometry). */
export function hash2(x: number, z: number, salt = 0): number {
  let h = Math.imul((x * 73856093) ^ (z * 19349663) ^ (salt * 83492791), 2654435761);
  h ^= h >>> 15;
  h = Math.imul(h, 2246822519);
  h ^= h >>> 13;
  h = Math.imul(h, 3266489917);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

export function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967296;
}
